import { useId } from 'react';
import { Check, TriangleAlert } from 'lucide-react';
import type { TimesheetTotals } from '@renewal/types';
import { hrs, isCoveredWeek, trackerStatusText, workedFillerLine } from '../utils/weekly-target.js';
import './WeeklyHoursTracker.css';

interface WeeklyHoursTrackerProps {
  totals: TimesheetTotals;
  /** The week is open. A submitted or approved week shows its hours with no call to action. */
  isEditable?: boolean;
}

/**
 * "Hours this week" in the timesheet's right column: the week against the
 * 40-hour rule (D-007), drawn only from the server's `totals.weeklyTarget`
 * (whole minutes, never summed decimal hours).
 *
 * - Short: brand green on a neutral track, "33.5 of 40 hrs, 6.5 to go".
 * - Ready (exactly 40, nothing else blocking): green text, a check, "Ready to
 *   submit". Exactly 40 with a blocker (say a day after today): "40 of 40 hrs"
 *   in neutral text, no check; the blockers are listed beside Submit.
 * - Over: the danger colour, a warning icon, "2 hrs over, reduce to submit".
 * - A week that is not open (submitted or approved): "40 of 40 hrs" in neutral
 *   text, with no check, warning or call to action.
 * - Weeks before the rule's start week: a plain total, no bar.
 *
 * State is always in the words; colour and icon only repeat it. The age-based
 * legal caps are not shown here; the server still enforces them on every save.
 */
export function WeeklyHoursTracker({ totals, isEditable = true }: WeeklyHoursTrackerProps) {
  const headingId = useId();
  const target = totals.weeklyTarget;

  if (!isCoveredWeek(target)) {
    const minutes = target?.totalMinutes ?? totals.weeklyMinutes ?? Math.round(totals.weekly * 60);
    return (
      <section
        className="weekly-hours-tracker weekly-hours-tracker--plain"
        aria-labelledby={headingId}
        data-testid="weekly-hours-tracker"
      >
        <h4 id={headingId} className="weekly-hours-tracker-title">
          Hours this week
        </h4>
        <p className="weekly-hours-tracker-status" data-testid="weekly-hours-status">
          {hrs(minutes)}
        </p>
      </section>
    );
  }

  const state = target.status;
  const statusText = trackerStatusText(target, isEditable);
  const fillPercent =
    state === 'short' ? Math.min(100, (target.totalMinutes / target.requiredMinutes) * 100) : 100;
  const requiredHours = target.requiredMinutes / 60;
  // The look follows what the person can do next: the check and green text
  // only when the server says the week is ready to submit.
  const look = !isEditable
    ? 'locked'
    : state === 'exact'
      ? target.ready
        ? 'ready'
        : 'blocked'
      : state;

  return (
    <section
      className={`weekly-hours-tracker weekly-hours-tracker--${look}`}
      aria-labelledby={headingId}
      data-testid="weekly-hours-tracker"
    >
      <h4 id={headingId} className="weekly-hours-tracker-title">
        Hours this week
      </h4>
      <div
        className="weekly-hours-tracker-bar"
        role="progressbar"
        aria-labelledby={headingId}
        aria-valuemin={0}
        aria-valuemax={requiredHours}
        aria-valuenow={Math.min(target.totalMinutes, target.requiredMinutes) / 60}
        aria-valuetext={statusText}
      >
        <div className="weekly-hours-tracker-fill" style={{ width: `${fillPercent}%` }} />
      </div>
      <p className="weekly-hours-tracker-status" data-testid="weekly-hours-status">
        {look === 'ready' && (
          <Check size={16} strokeWidth={2.5} className="weekly-hours-tracker-icon" aria-hidden />
        )}
        {look === 'over' && (
          <TriangleAlert
            size={16}
            strokeWidth={2.2}
            className="weekly-hours-tracker-icon"
            aria-hidden
          />
        )}
        <span>{statusText}</span>
      </p>
      {target.fillerMinutes > 0 && (
        <p className="weekly-hours-tracker-split" data-testid="weekly-hours-split">
          {workedFillerLine(target)}
        </p>
      )}
    </section>
  );
}
